import React, { useState } from 'react';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import type { Organization } from '@/api/services/organizations';

type Props = {
  organization: Organization;
  onClose: () => void;
  onConfirm: (confirmationName: string) => Promise<void>;
};

export default function DeleteOrganizationModal({ organization, onClose, onConfirm }: Props) {
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canDelete = confirmation.trim() === organization.name.trim();

  async function handleConfirm() {
    if (!canDelete) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(confirmation.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setSubmitting(false);
    }
  }

  return (
    <ConfirmDestructiveDialog
      title="Delete organization"
      onClose={onClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={submitting || !canDelete}
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <p className="muted modal-hint">This action is irreversible.</p>
      <p>Deleting this organization removes:</p>
      <ul className="removal-summary-list">
        <li>workspace memberships</li>
        <li>searches</li>
        <li>subscriptions</li>
        <li>templates</li>
        <li>recipients</li>
        <li>watches</li>
        <li>dashboards</li>
        <li>organization settings</li>
        <li>source assignments</li>
      </ul>
      <p className="muted">Platform users are NOT deleted. Members keep their personal workspace.</p>
      <p>
        Organization: <strong>{organization.name}</strong>
      </p>
      <label htmlFor="delete-org-confirm-name">Type organization name to confirm:</label>
      <input
        id="delete-org-confirm-name"
        value={confirmation}
        onChange={(e) => setConfirmation(e.target.value)}
        placeholder={organization.name}
        autoComplete="off"
      />
      {error ? <div className="alert alert-error">{error}</div> : null}
    </ConfirmDestructiveDialog>
  );
}
